"use client"

import React, { useCallback, useState } from 'react'
import { Textarea, type TextareaProps } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { MarkdownToolbar, handleMarkdownShortcut } from '@/components/markdown-toolbar'
import { VoiceInputButton, type VoiceInputState } from './voice-input-button'
import { useFieldVoice } from './use-field-voice'
import { useVoiceAvailable } from './use-voice-available'

export interface VoiceTextareaProps extends TextareaProps {
  /** Явное применение нового значения (основной путь) — см. VoiceInput */
  onVoiceInsert?: (next: string) => void
  /** Классы обёртки (`relative`): сюда переносятся ширина/flex/высота-контейнера */
  wrapperClassName?: string
  onVoiceStateChange?: (state: VoiceInputState) => void
  noVoice?: boolean
  /** Фон: `muted` (по умолчанию) — серое поле без рамки, как в мобильных формах; `default` — обычный Textarea */
  tone?: 'muted' | 'default'
  /** Панель markdown-форматирования над полем и сочетания Ctrl+B/I/K (значение хранится как markdown) */
  formatting?: boolean
}

/** Серое многострочное поле (общий вид текстовых полей описания/комментария на desktop) */
export const TEXTAREA_MUTED_CLS = 'bg-muted border-transparent shadow-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0'


/**
 * Drop-in замена `Textarea` с кнопкой микрофона в правом нижнем углу (пилюля растёт влево).
 * Если голосовой ввод недоступен — рендерит ровно обычный `Textarea`.
 */
export const VoiceTextarea = React.forwardRef<HTMLTextAreaElement, VoiceTextareaProps>(function VoiceTextarea(
  { onVoiceInsert, wrapperClassName, onVoiceStateChange, noVoice, tone = 'muted', formatting, className, disabled, readOnly, onBlur, onSelect, onKeyUp, onClick, onKeyDown, ...props },
  ref,
) {
  const available = useVoiceAvailable()
  const showVoice = available && !noVoice && !disabled && !readOnly
  const showFormatting = !!formatting && !disabled && !readOnly

  const toneCls = tone === 'muted' ? TEXTAREA_MUTED_CLS : undefined
  const [voiceState, setVoiceState] = useState<VoiceInputState>('idle')
  const { fieldRef, insert, bindSelectionTracking } = useFieldVoice<HTMLTextAreaElement>({ onVoiceInsert, uncontrolled: props.value === undefined })

  const setRefs = useCallback((el: HTMLTextAreaElement | null) => {
    fieldRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) ref.current = el
  }, [fieldRef, ref])

  const handleStateChange = useCallback((s: VoiceInputState) => {
    setVoiceState(s)
    onVoiceStateChange?.(s)
  }, [onVoiceStateChange])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showFormatting && handleMarkdownShortcut(e)) return
    onKeyDown?.(e)
  }, [showFormatting, onKeyDown])

  const toolbar = showFormatting ? <MarkdownToolbar targetRef={fieldRef} className="mb-1 -ml-1" /> : null

  if (!showVoice) {
    const field = (
      <Textarea
        ref={setRefs}
        disabled={disabled}
        readOnly={readOnly}
        className={cn(toneCls, className)}
        onBlur={onBlur}
        onSelect={onSelect}
        onKeyUp={onKeyUp}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        {...props}
      />
    )
    if (!toolbar) return field
    return <div className={wrapperClassName}>{toolbar}{field}</div>
  }

  const voiceField = (
    <>
      <Textarea
        ref={setRefs}
        disabled={disabled}
        readOnly={readOnly}
        className={cn(toneCls, className, 'pr-10', voiceState === 'error' && 'border-red-500')}
        onKeyDown={handleKeyDown}
        {...bindSelectionTracking({ onBlur, onSelect, onKeyUp, onClick })}
        {...props}
      />
      <div className="absolute bottom-1.5 right-1.5 flex items-center justify-end">
        <VoiceInputButton
          size="md"
          placement="inline"
          onInsert={insert}
          onStateChange={handleStateChange}
        />
      </div>
    </>
  )

  if (toolbar) {
    return (
      <div className={wrapperClassName} data-voice-error={voiceState === 'error' ? '' : undefined}>
        {toolbar}
        <div className="relative">{voiceField}</div>
      </div>
    )
  }

  return (
    <div
      className={cn('relative', wrapperClassName)}
      data-voice-error={voiceState === 'error' ? '' : undefined}
    >
      {voiceField}
    </div>
  )
})
